import type { ChangeEvent, ComponentProps } from 'react';
import { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import {
    FormInputContainer,
    StyledInput,
    StyledError,
    EdgeShadow,
} from './styled';

import { Range } from '../../constants/inputs';

type ISLiderProps = ComponentProps<typeof FormInputContainer> & {
    id: string;
    disabled?: boolean;
    min?: number;
    max?: number;
};

const STEP_SIZE = 2; // In percents of the track

//
// In order to work properly, this component needs to be in some FormProvider
//
export function FormSlider({ id, disabled, min, max, ...props }: ISLiderProps) {
    const { watch } = useFormContext();
    const {
        field: { onChange, onBlur },
        fieldState: { error },
    } = useController({ name: id });

    const minId = `${id}.${Range.MIN}`;
    const maxId = `${id}.${Range.MAX}`;

    const [minVal, maxVal]: [string, string] = watch([minId, maxId]);

    const onLowerChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            const value = Number(ev.target.value);
            const lowerBound = min ?? Number.MIN_VALUE;
            const upperBound = (Number(maxVal) ?? max ?? Number.MAX_VALUE) - 1;
            if (value >= lowerBound && value <= upperBound) {
                onChange({
                    [Range.MIN]: value.toString(),
                    [Range.MAX]: maxVal,
                });
                onBlur();
            }
        },
        [maxVal, onChange, min, max, onBlur]
    );

    const onUpperChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            const value = Number(ev.target.value);
            const lowerBound = (Number(minVal) ?? min ?? Number.MIN_VALUE) + 1;
            const upperBound = max ?? Number.MAX_VALUE;
            if (value >= lowerBound && value <= upperBound) {
                onChange({
                    [Range.MIN]: minVal,
                    [Range.MAX]: value.toString(),
                });
                onBlur();
            }
        },
        [minVal, onChange, max, min, onBlur]
    );

    const percentSize =
        min !== undefined && max !== undefined ? (max - min) / 100 : 1;
    const step = Math.ceil(percentSize / STEP_SIZE);

    return (
        <FormInputContainer {...props}>
            <StyledInput
                hasError={Boolean(error)}
                type="range"
                disabled={disabled}
                min={min}
                max={max}
                onChange={onLowerChange}
                onBlur={onBlur}
                value={minVal || ''}
                step={step}
            />
            <StyledInput
                hasError={Boolean(error)}
                type="range"
                disabled={disabled}
                min={min}
                max={max}
                onChange={onUpperChange}
                onBlur={onBlur}
                value={maxVal || ''}
                step={step}
            />
            <EdgeShadow
                lowerBound={Number(minVal) / percentSize}
                upperBound={Number(maxVal) / percentSize}
            />
            <StyledError>{error?.message}</StyledError>
        </FormInputContainer>
    );
}

FormSlider.defaultProps = {
    disabled: false,
    min: undefined,
    max: undefined,
};
